/* istanbul ignore file */

import React from 'react';
import { Switch, Route } from '@react-router';
import { JoyKitThemeProvider, createGlobalStyle, Box, GridOverlay, NoSsr } from '@withjoy/joykit';

import '@assets/charm.css';
import { TelemetryProvider } from './GuestSite.telemetry';

import { withWindow } from '@shared/utils/withWindow';
import { HotelBooking } from './routes/HotelBooking';
import { MediaCollection } from './routes/MediaCollection';
import loadable from '@loadable/component';
import { StandAloneAlohaMenu } from './packages/layout-engine/components/StandAloneAlohaMenu';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useHotelRoutePaths } from './routes/HotelBooking/HotelBooking.routes';
import { EventHandleFetcher } from './components/EventHandleFetcher';
import { UUID_ROUTE_PATTERN } from '@shared/utils/uuid';

const GuestSite = loadable(
  () =>
    import(
      /* webpackChunkName: "content/guest/site" */
      './routes/GuestSite'
    ),
);

const EventAccommodationsApp = loadable(
  () =>
    import(
      /* webpackChunkName: "content/accommodations" */
      '@apps/stays/EventAccommodations'
    ),
);

const HotelSearch = loadable(
  () =>
    import(
      /* webpackChunkName: "content/hotel/search" */
      '@apps/guest/routes/HotelBooking/routes/HotelSearch'
    ),
);

const BookYourStay = loadable(
  () =>
    import(
      /* webpackChunkName: "content/hotel/bookyourstay" */
      '@apps/guest/routes/HotelBooking/routes/BookYourStay'
    ),
);

const HotelBookingPartnerSearch = loadable(
  () =>
    import(
      /* webpackChunkName: "content/hotel/partnersearch" */
      '@apps/guest/routes/HotelBooking/routes/HotelBookingPartnerSearch'
    ),
);

const AccommodationPlacePartner = loadable(
  () =>
    import(
      /* webpackChunkName: "content/hotel/accommodationplacepartner" */
      '@apps/guest/routes/HotelBooking/routes/AccommodationPlacePartner'
    ),
);

const Collaborators = loadable(
  () =>
    import(
      /* webpackChunkName: "content/guest/collaborators" */
      '@apps/guest/routes/Collaborators/Collaborators'
    ),
);

const CashFundGuest = loadable(
  () =>
    import(
      /* webpackChunkName: "content/cashfunds/guest" */
      '@apps/guest/routes/CashFunds/routes/CashFundGuest/CashFundGuest'
    ),
);

const HotelDetails = loadable(
  () =>
    import(
      /* webpackChunkName: "content/hotel/details" */
      '@apps/guest/routes/HotelBooking/routes/HotelDetails'
    ),
);

const EventAccommodationsWrapper = ({ eventHandle }: { eventHandle: string }) => {
  const isMobile = useIsMobileScreen();
  const isNewMobilePDPEnabled = useFeatureValue('accomodationsNewMobilePDPEnabled', { skip: !isMobile }).value === 'on';

  return (
    <>
      <EventAccommodationsApp eventHandle={eventHandle} />
      {!isNewMobilePDPEnabled && <StandAloneAlohaMenu eventHandle={eventHandle} />}
    </>
  );
};

const GlobalStyle = createGlobalStyle`
  body {
    font-family: 'Gotham SSm';
  }
`;

export interface GuestProps {}

const Guest: React.FC<GuestProps> = () => {
  const hotelRoutePaths = useHotelRoutePaths();
  return (
    <JoyKitThemeProvider>
      <GlobalStyle />
      <Box
        as="main"
        overflowX={['hidden', null, 'initial']}
      >
        <NoSsr>
          <GridOverlay />
        </NoSsr>
        <Switch>
          <Route
            path={`/stays/:joyPlaceId(${UUID_ROUTE_PATTERN})`}
            render={() => {
              return <EventHandleFetcher>{eventHandle => <EventAccommodationsWrapper eventHandle={eventHandle} />}</EventHandleFetcher>;
            }}
          />
          <Route
            path="/:eventHandle/moments"
            render={() => {
              // moments is an app-browser page, need to reload to let charm-server 307
              withWindow(() => window.location.reload());
              return null;
            }}
          />
          <Route
            path="/:eventHandle/registry"
            render={() => {
              withWindow(() => window.location.reload());
              return null;
            }}
          />
          <Route
            path="/:eventHandle/accommodations/:accommodationId"
            render={({ match }) => {
              return <EventAccommodationsWrapper eventHandle={match.params.eventHandle} />;
            }}
          />
          <Route
            path="/:eventHandle/hotels/:joyPlaceId"
            render={({ match }) => {
              return <EventAccommodationsWrapper eventHandle={match.params.eventHandle} />;
            }}
          />
          <Route
            path={hotelRoutePaths.accommodationPlace.path}
            render={({ match }) => {
              return <AccommodationPlacePartner accommodationPlaceV2ByIdId={match.params.accommodationPlaceId} />;
            }}
          />
          <Route
            path={'/:eventHandle/hotel'}
            exact
            render={() => <HotelBookingPartnerSearch />}
          />
          <Route
            path={hotelRoutePaths.hotelSearch.path}
            render={({ match }) => <HotelSearch eventHandle={match.params.eventHandle} />}
          />
          <Route
            path={hotelRoutePaths.hotelDetails.path}
            render={({ match }) => (
              <HotelDetails
                joyPlaceId={match.params.joyPlaceId}
                eventHandle={match.params.eventHandle}
              />
            )}
          />
          <Route
            path="/:eventHandle/hotelbooking"
            render={({ match }) => {
              const eventHandle = match.params.eventHandle;
              return <HotelBooking eventHandle={eventHandle || null} />;
            }}
          />
          <Route
            path={hotelRoutePaths.bookYourStay.path}
            render={({ match }) => {
              const eventHandle = match.params.eventHandle;
              return <BookYourStay eventHandle={eventHandle || null} />;
            }}
          />
          <Route
            path="/:eventHandle/page/:pageSlug"
            render={({ match, staticContext }) => {
              const eventHandle = match.params.eventHandle;
              return (
                <>
                  {match.params.pageSlug && (
                    <TelemetryProvider context={{ eventName: eventHandle }}>
                      <GuestSite
                        eventHandle={eventHandle || null}
                        staticContext={staticContext}
                      />
                    </TelemetryProvider>
                  )}
                </>
              );
            }}
          />
          <Route
            path="/:eventHandle/albums"
            render={({ match }) => {
              const eventHandle = match.params.eventHandle;
              return (
                <>
                  <TelemetryProvider context={{ eventName: eventHandle }}>
                    <MediaCollection eventHandle={eventHandle || null} />
                  </TelemetryProvider>
                </>
              );
            }}
          />
          <Route
            path="/:eventHandle/collaborators"
            render={({ match }) => {
              const eventHandle = match.params.eventHandle;
              return (
                <TelemetryProvider context={{ eventName: eventHandle }}>
                  <Collaborators eventHandle={eventHandle || null} />
                </TelemetryProvider>
              );
            }}
          />
          <Route
            path="/:eventHandle/cash-fund/:fundId"
            render={({ match, staticContext }) => {
              const eventHandle = match.params.eventHandle;
              const fundId = match.params.fundId;
              return (
                <TelemetryProvider context={{ eventName: eventHandle }}>
                  <CashFundGuest
                    eventHandle={eventHandle || null}
                    fundId={fundId || null}
                    staticContext={staticContext}
                  />
                </TelemetryProvider>
              );
            }}
          />
          <Route
            path="/:eventHandle"
            render={({ match, staticContext }) => {
              const eventHandle = match.params.eventHandle;
              return (
                <TelemetryProvider context={{ eventName: eventHandle }}>
                  <GuestSite
                    eventHandle={eventHandle || null}
                    staticContext={staticContext}
                  />
                </TelemetryProvider>
              );
            }}
          />
        </Switch>
      </Box>
    </JoyKitThemeProvider>
  );
};

Guest.displayName = 'Guest';

export default Guest;
